import type { ReactNode } from "react";
import { useEffect } from "react";
import type { QueryClient } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  HeadContent,
  Scripts,
  useRouterState,
} from "@tanstack/react-router";
import { isApiError } from "@majourneys/api-client";
import { captureFirstTouch, captureReferral, setAdConsentCheck } from "@majourneys/booking";
import { AppRoot } from "../app-root";
import type { authClient } from "../lib/auth";
import { apiBase } from "../lib/api-base";
import { BLUR_UP_SCRIPT } from "../lib/blur-up";
import { STALE_RELOAD_SCRIPT } from "../lib/stale-deploy";
import { ErrorFallback } from "../shared/ui/error-fallback";
import { NotFound } from "../shared/ui/not-found";
import { RouteProgressBar } from "../shared/ui/route-progress-bar";
import { DEFAULT_LOCALE, LOCALE_URLS, RTL_LOCALES, SUPPORTED_LOCALES, type SupportedLocale } from "../lib/i18n";
import { readSessionHintFromRequest } from "../lib/session-hint";
import { readFxSnapshot } from "../lib/fx-snapshot";
import type { FxSnapshot } from "@majourneys/currency";
import geistFontUrl from "@fontsource-variable/geist/files/geist-latin-wght-normal.woff2?url";
import { GOOGLE_ADS_BOOTSTRAP } from "../shared/consent/ad-pixels-loader";
import { readAnalyticsConsent } from "../shared/consent/analytics-consent";
import { OfflineBanner } from "../shared/layout/offline-banner";
import poppins400Url from "@fontsource/poppins/files/poppins-latin-400-normal.woff2?url";
import poppins500Url from "@fontsource/poppins/files/poppins-latin-500-normal.woff2?url";
import poppins600Url from "@fontsource/poppins/files/poppins-latin-600-normal.woff2?url";
import poppins700Url from "@fontsource/poppins/files/poppins-latin-700-normal.woff2?url";

// The Meta and TikTok pixels load only after cookie consent, and Google Ads
// starts with every storage type denied (Consent Mode): see
// shared/consent/ad-pixels-loader.ts.

// Network-wide Organization JSON-LD — declares the 4 city + 7 attraction
// sister sites under MaJourneys so Google + AI engines understand the
// cross-site link graph instead of mistaking it for a PBN. Kept inline
// here (rather than imported from @majourneys/attraction-template) so the
// majourneys app's type-check doesn't pull the package's Footer + Header
// React components — which target the attraction routeTree, not this one
// — into scope.
const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "TravelAgency",
  // The id every page's provider/seller/publisher points at, so those
  // references resolve on every page, not only on home.
  "@id": "https://majourneys.com/#organization",
  name: "MaJourneys",
  url: "https://majourneys.com",
  logo: "https://majourneys.com/images/majourneys-logo.png",
  sameAs: [
    "https://visit-fes.com",
    "https://visit-tetouan.com",
    "https://explore-rabat.com",
    "https://explore-casablanca.com",
    "https://hassan-mosque.com",
    "https://koutoubia-mosque.com",
    "https://ait-ben-haddou.com",
    "https://akchour-waterfalls.com",
    "https://ouzoud-waterfalls.com",
    "https://chouara-tannery.com",
    "https://visit-volubilis.com",
  ],
} as const;

type SessionData = Awaited<ReturnType<typeof authClient.getSession>>["data"];

export interface RouterContext {
  queryClient: QueryClient;
  session?: SessionData | null;
  hasSessionHint?: boolean;
  /** The currency and rates this page was server-rendered with (lib/fx-snapshot.ts). */
  fx?: FxSnapshot | null;
}

function resolveLocale(pathname: string): SupportedLocale {
  const first = pathname.split("/").filter(Boolean)[0];
  return (SUPPORTED_LOCALES as readonly string[]).includes(first ?? "")
    ? (first as SupportedLocale)
    : DEFAULT_LOCALE;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  // No session fetch here — public routes don't need it. `useSession()`
  // hooks in the header/avatar/etc. fetch on mount and cache in better-auth.
  // `_authed.tsx` does the synchronous guard for protected subtrees.
  // Cheap server-only cookie probe so SSR can render the correct auth slot
  // shape (avatar skeleton vs Sign-In) without an API hop. The cookie is
  // HttpOnly so the client cannot read it; the bool is serialized into the
  // router context during SSR and reused at hydration.
  // The currency snapshot rides along the same way, so prices paint once in
  // the guest's currency at the live rate (lib/fx-snapshot.ts).
  beforeLoad: async () => ({
    session: null,
    hasSessionHint: readSessionHintFromRequest(),
    fx: await readFxSnapshot(),
  }),
  head: ({ matches, match }) => {
    // The root match's own pathname is "/"; the page locale lives in the
    // deepest match, whose pathname is the full URL path.
    const lang = resolveLocale(matches[matches.length - 1]?.pathname ?? match.pathname);
    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        { name: "theme-color", content: "#050036" },
      ],
      // The `/@tanstack-start/styles.css` URL is a Vite dev-server virtual
      // module — it does not exist in the production build, so emitting it
      // in prod yields a 404 (or HTML fallback under Cloudflare Workers'
      // assets binding) and a MIME-type error that breaks hydration.
      links: [
        // The icons were in /public but never linked: browsers only found
        // /favicon.ico by guessing, the SVG and the home-screen icon were
        // never used, and the manifest's icons did not exist (owner,
        // 2026-09-26). SVG first for modern browsers, the .ico as fallback.
        { rel: "icon", href: "/favicon.svg", type: "image/svg+xml" },
        { rel: "icon", href: "/favicon.ico", sizes: "48x48" },
        { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
        { rel: "manifest", href: "/site.webmanifest" },
        { rel: "preconnect", href: "https://api.majourneys.com", crossOrigin: "anonymous" },
        { rel: "preconnect", href: "https://imagedelivery.net", crossOrigin: "anonymous" },
        {
          rel: "preload",
          as: "font",
          href: geistFontUrl,
          type: "font/woff2",
          crossOrigin: "anonymous",
        },
        // Every Poppins weight the site uses, not just 400: 500 was only
        // discovered 3s in on list pages and its late swap shifted the cards
        // (Clarity CLS, 2026-10-10). About 8 KB each.
        ...[poppins400Url, poppins500Url, poppins600Url, poppins700Url].map((href) => ({
          rel: "preload",
          as: "font",
          href,
          type: "font/woff2",
          crossOrigin: "anonymous" as const,
        })),
        // The page locale's strings, fetched by src/client.tsx before hydration.
        // `crossOrigin: "anonymous"` matches fetch()'s default same-origin
        // credentials mode; without it the browser would download twice.
        ...(lang === "en"
          ? []
          : [
              {
                rel: "preload",
                as: "fetch",
                href: LOCALE_URLS[lang],
                crossOrigin: "anonymous" as const,
              },
            ]),
        ...(import.meta.env.DEV
          ? [{ rel: "stylesheet", href: "/@tanstack-start/styles.css?routes=__root__" }]
          : []),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(ORGANIZATION_SCHEMA),
        },
        // Google Ads tag (gtag.js) — account AW-969299995. Loads site-wide so
        // click data (GCLID) is captured on landing; the actual conversion
        // event fires on the booking thank-you page (packages/booking).
        {
          src: "https://www.googletagmanager.com/gtag/js?id=AW-969299995",
          async: true,
        },
        // Consent Mode defaults ("denied") before `config`; the banner's
        // "Accept" switches them on (shared/consent/ad-pixels-loader.ts).
        { children: GOOGLE_ADS_BOOTSTRAP },
      ],
    };
  },
  errorComponent: ({ error }) => {
    if (isApiError(error) && error.status === 404) return <NotFound />;
    // A chunk that vanished in a deploy is handled inside ErrorFallback, which
    // every route's error boundary shares (router.tsx defaultErrorComponent).
    return <ErrorFallback error={error} />;
  },
  component: RootComponent,
});

function RootComponent() {
  const { queryClient, fx } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const lang = resolveLocale(pathname);

  // Record how this visitor reached us, on the first page they land on: the
  // referrer and any ?utm_source only exist there, and checkout is several
  // navigations later. No-ops on every subsequent page. The ad click id it
  // holds in memory reaches a booking only once the banner says "granted".
  useEffect(() => {
    setAdConsentCheck(() => readAnalyticsConsent() === "granted");
    captureFirstTouch();
    // Ambassador `?ref=`: last click wins for 30 days. Runs on first mount
    // for the same reason as above: the router drops the query right after.
    captureReferral(apiBase());
  }, []);

  /**
   * The soft-edge audit, on every route change, in dev only.
   *
   * Three separate defects were reported from screenshots on 2026-08-27 — a
   * rectangle under a card rail, a hard line across an entry screen, a seam
   * along a glass bar — and all three were the same bug: a soft effect whose
   * visual reach runs past its layout box, cut square by something that clips.
   * A phone frame must clip and this design language is entirely soft, so the
   * two are in permanent conflict and every screen carries dozens of latent
   * seams.
   *
   * They were being caught by a person looking at a picture. This makes them
   * catchable before the picture exists.
   *
   * Bundlers drop the whole block on `import.meta.env.DEV`, and the import is
   * dynamic so the module never reaches a production chunk at all.
   */
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    let cancelled = false;
    // Two frames: one still catches routes that animate in mid-layout, and a
    // false positive teaches people to ignore the output.
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(async () => {
        if (cancelled) return;
        const { reportSoftEdges } = await import("../lib/dev/soft-edge-audit");
        reportSoftEdges();
      }),
    );
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [pathname]);

  return (
    <RootDocument lang={lang}>
      <AppRoot queryClient={queryClient} lang={lang} fx={fx ?? null}>
        <Outlet />
        {/* Every page, the account included: the connection is not a route. */}
        <OfflineBanner />
      </AppRoot>
    </RootDocument>
  );
}

function RootDocument({
  lang,
  children,
}: Readonly<{ lang: SupportedLocale; children: ReactNode }>) {
  const dir = RTL_LOCALES.includes(lang) ? "rtl" : "ltr";
  return (
    <html lang={lang} dir={dir}>
      <head>
        <HeadContent />
        <script
          dangerouslySetInnerHTML={{
            __html: "globalThis.__name||(globalThis.__name=(t,n)=>Object.defineProperty(t,'name',{value:n,configurable:!0}));",
          }}
        />
        {/* Before <body>: shows each card photo the moment it loads, without
            waiting for React. See lib/blur-up.ts. */}
        <script dangerouslySetInnerHTML={{ __html: BLUR_UP_SCRIPT }} />
        {/* Clarity script injected by the Worker (apps/majourneys/src/server.ts)
            from the runtime VITE_CLARITY_PROJECT_ID var — survives every deploy. */}
      </head>
      <body>
        <RouteProgressBar />
        {children}
        <Scripts />
        {/* When a hashed Vite chunk is missing (typical after a redeploy where
            the previous build's chunk filenames no longer exist), Vite fires
            `vite:preloadError`. The default behavior surfaces as an uncaught
            promise rejection and the SPA gets stuck. Reload once with a cache
            buster so the browser fetches the fresh HTML (which references the
            new chunk hashes). Guards are explained in lib/stale-deploy.ts. */}
        <script
          dangerouslySetInnerHTML={{
            __html: STALE_RELOAD_SCRIPT,
          }}
        />
      </body>
    </html>
  );
}
